<script>
// A pure numeric range min, min+step, min+2*step, ... up to (and including,
// within floating-point tolerance) max. `capCount` guards against a stray
// tiny step producing an effectively unbounded array — rather than reject
// it, the step is widened just enough to fit within the cap, so a bad input
// still renders SOME table instead of hanging the tab.
function buildRange(min, max, step, capCount) {
  if (!(step > 0) || !(max >= min)) return [];

  let count = Math.floor((max - min) / step + 1e-9) + 1;
  if (count > capCount) {
    step = (max - min) / (capCount - 1);
    count = capCount;
  }

  const result = [];
  for (let i = 0; i < count; i++) {
    result.push(min + i * step);
  }
  return result;
}

// Repeated float addition in buildRange can leave a value like
// 29.999999999996 instead of 30 — round before formatting so labels don't
// show spurious decimals.
function roundNice(n) {
  return Math.round(n * 1e6) / 1e6;
}

function formatNum(n) {
  const r = roundNice(n);
  return Number.isInteger(r) ? r.toString() : r.toFixed(1);
}
</script>

<script setup>
import { ref, computed, watch } from 'vue';
import { useShared } from '../state.js';
import { usePersisted } from '../use_persisted.js';
import { scopedKey, useSetups } from '../setups.js';
import { useToasts } from '../use_toasts.js';
import { useScrollEdges } from '../use_scroll_edges.js';
import { useDeferredCompute } from '../use_deferred_compute.js';
import * as calc from '../calc.js';
import RecomputeButton from './recompute_button.vue';
import UnitSelect from './unit_select.vue';

const { inputs, computedResult, isStale } = useShared();
const { pushToast } = useToasts();
const { activeSetupId } = useSetups();

const downhillScrollWrapper = ref(null);
const { canScrollLeft: downhillCanScrollLeft, canScrollRight: downhillCanScrollRight } = useScrollEdges(downhillScrollWrapper);
const uphillScrollWrapper = ref(null);
const { canScrollLeft: uphillCanScrollLeft, canScrollRight: uphillCanScrollRight } = useScrollEdges(uphillScrollWrapper);

// New key/shape (grid of distance x angle rather than a single cut angle
// against a max distance) — a fresh key avoids handing old sessions'
// {unit, max_distance, cut} straight into fields it doesn't have.
const input = usePersisted(scopedKey('cuts-table-grid-inputs', activeSetupId.value), {
  unit: 'm',
  min_distance: 10,
  max_distance: 70,
  distance_spacing: 5,
  max_angle: 40,
  angle_spacing: 5,
});

let lastCutsTableError = null;

const MAX_DISTANCES = 200;
const MAX_ANGLES = 100;

// Known synchronously, with no physics involved — how many rows/columns the
// tables will end up with, regardless of what's actually in them. Used to
// size the placeholder rows below to the real tables' eventual height.
const distanceCount = computed(() => {
  const min = Number(input.value.min_distance) || 0;
  const max = Math.max(Number(input.value.max_distance) || 0, min);
  const spacing = Math.max(Number(input.value.distance_spacing) || 0, 0.01);
  return buildRange(min, max, spacing, MAX_DISTANCES).length;
});

const angleCount = computed(() => {
  const max = Math.min(Math.max(Number(input.value.max_angle) || 0, 0), 89);
  const spacing = Math.max(Number(input.value.angle_spacing) || 0, 0.1);
  return buildRange(0, max, spacing, MAX_ANGLES).length;
});

// results/computing replace what used to be a plain computed() — a
// computed() getter can't yield mid-evaluation, and the whole point here
// is to break the loop into chunks (see yieldToFrame in the loop below)
// so it doesn't run as one uninterrupted block long enough to freeze the
// panel's own open animation. The watch below replicates the same
// reactive dependencies a computed() would have picked up automatically.
const angleLabels = ref([]);
const downhillRows = ref([]);
const uphillRows = ref([]);
const computing = ref(true);

async function computeGrids() {
  computing.value = true;

  if (isStale.value) {
    console.log("calculated is stale, cannot compute cuts table");
    downhillRows.value = [];
    uphillRows.value = [];
    computing.value = false;
    return;
  }

  const minDistance = Math.max(Number(input.value.min_distance) || 0, 0);
  const maxDistance = Math.max(Number(input.value.max_distance) || 0, minDistance);
  const distanceSpacing = Math.max(Number(input.value.distance_spacing) || 0, 0.01);
  const maxAngle = Math.min(Math.max(Number(input.value.max_angle) || 0, 0), 89);
  const angleSpacing = Math.max(Number(input.value.angle_spacing) || 0, 0.1);

  const unit = input.value.unit;
  const scale = calc.toScale(unit);

  const distances = buildRange(minDistance, maxDistance, distanceSpacing, MAX_DISTANCES);
  const angles = buildRange(0, maxAngle, angleSpacing, MAX_ANGLES);

  const r = computedResult.value;
  const settings = inputs.sight_settings;
  const barPosition = settings.sight_bar_position;

  const newDownhillRows = [];
  const newUphillRows = [];

  try {
    // The whole grid (Downhill and Uphill, every cell) in one call — see
    // calc.cutsGrid / rust/calc-core's compute_cuts_grid: computed entirely
    // in Rust, reusing the shared level-ground table (see state.js's
    // getLevelMarksTable) without that table — or the per-cell search
    // loop — ever crossing the wasm boundary; only this small grid of
    // results does. distances/angles cross in meters/degrees; cells come
    // back in meters, or null where the target mark isn't achievable at
    // any distance (rendered as "—" below).
    const distancesM = distances.map(d => d * scale);
    const { downhill, uphill } = calc.cutsGrid(settings, barPosition, r, distancesM, angles);

    const formatCell = (cellM) => cellM === null ? '—' : (cellM / scale).toFixed(2) + " " + unit;

    distances.forEach((d, i) => {
      newDownhillRows.push({ distance: formatNum(d) + unit, cells: downhill[i].map(formatCell) });
      newUphillRows.push({ distance: formatNum(d) + unit, cells: uphill[i].map(formatCell) });
    });

    lastCutsTableError = null;

  } catch (error) {
    console.log('error computing cuts table:', error.message);
    console.log(error.stack);
    if (error.message !== lastCutsTableError) {
      lastCutsTableError = error.message;
      pushToast(`Cuts Table: ${error.message}`);
    }
  }

  angleLabels.value = angles.map(a => formatNum(a) + '°');
  downhillRows.value = newDownhillRows;
  uphillRows.value = newUphillRows;
  computing.value = false;
}

// The initial run is deferred (see use_deferred_compute.js) so the
// panel's own open animation gets to paint and start before this begins —
// a later recompute (the watch below, edited inputs/settings) isn't
// deferred since the panel is already open by then, with no animation
// racing it, but it's still chunked the same way so a big table doesn't
// freeze the page either way.
useDeferredCompute(() => computeGrids());

watch([computedResult, input, () => inputs.sight_settings], () => {
  computeGrids();
}, { deep: true });

const copied = ref(false);

function copyTable() {
  const headers = ['Distance', ...angleLabels.value];

  const downhillText = ['Downhill', headers.join('\t'),
    ...downhillRows.value.map(row => [row.distance, ...row.cells].join('\t'))].join('\n');
  const uphillText = ['Uphill', headers.join('\t'),
    ...uphillRows.value.map(row => [row.distance, ...row.cells].join('\t'))].join('\n');

  const text = `${downhillText}\n\n${uphillText}`;

  navigator.clipboard.writeText(text)
    .then(() => {
      copied.value = true;
      setTimeout(() => { copied.value = false; }, 2000);
    })
    .catch(e => console.warn('Failed to copy:', e));
}

</script>

<template>
  <div class="centered">
    <RecomputeButton />

    <div class="controls">
      <label>Unit:
        <UnitSelect v-model="input.unit" />
      </label>

      <label>Min distance:
        <input type="number" class="input-measure" v-model.number="input.min_distance" />
      </label>

      <label>Max distance:
        <input type="number" class="input-measure" v-model.number="input.max_distance" />
      </label>

      <label>Distance spacing:
        <input type="number" class="input-measure" v-model.number="input.distance_spacing" />
      </label>

      <label>Max angle:
        <input type="number" class="input-measure" v-model.number="input.max_angle" />
      </label>

      <label>Angle spacing:
        <input type="number" class="input-measure" v-model.number="input.angle_spacing" />
      </label>

      <button @click="copyTable" class="btn-copy">
        {{ copied ? 'Copied!' : 'Copy Table' }}
      </button>
    </div>

    <h3>Downhill</h3>
    <div class="table-scroll-outer">
      <div class="table-scroll-wrapper" ref="downhillScrollWrapper">
        <table>
          <thead>
            <tr>
              <th>Distance</th>
              <th v-for="(label, c) in angleLabels" :key="c">{{ label }}</th>
            </tr>
          </thead>
          <!-- While computing, blank rows in the exact final row/column
               count (known synchronously above, without touching any
               physics) hold the table at the same size it'll end up
               needing, so the panel doesn't animate open small and then
               jump bigger once the real rows replace these. -->
          <tbody v-if="computing">
            <tr v-for="n in distanceCount" :key="n" class="placeholder-row">
              <td v-for="c in (angleCount + 1)" :key="c">&nbsp;</td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr v-for="(row, r) in downhillRows" :key="r">
              <td>{{ row.distance }}</td>
              <td v-for="(cell, c) in row.cells" :key="c">{{ cell }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="scroll-shadow scroll-shadow-left" :class="{ visible: downhillCanScrollLeft }"></div>
      <div class="scroll-shadow scroll-shadow-right" :class="{ visible: downhillCanScrollRight }"></div>
    </div>

    <h3>Uphill</h3>
    <div class="table-scroll-outer">
      <div class="table-scroll-wrapper" ref="uphillScrollWrapper">
        <table>
          <thead>
            <tr>
              <th>Distance</th>
              <th v-for="(label, c) in angleLabels" :key="c">{{ label }}</th>
            </tr>
          </thead>
          <tbody v-if="computing">
            <tr v-for="n in distanceCount" :key="n" class="placeholder-row">
              <td v-for="c in (angleCount + 1)" :key="c">&nbsp;</td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr v-for="(row, r) in uphillRows" :key="r">
              <td>{{ row.distance }}</td>
              <td v-for="(cell, c) in row.cells" :key="c">{{ cell }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="scroll-shadow scroll-shadow-left" :class="{ visible: uphillCanScrollLeft }"></div>
      <div class="scroll-shadow scroll-shadow-right" :class="{ visible: uphillCanScrollRight }"></div>
    </div>
  </div>

</template>

<style scoped>

.controls {
  display: flex;
  gap: var(--space-lg);
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: var(--space-sm);
}

h3 {
  margin: var(--space-lg) 0 var(--space-sm);
  text-align: center;
}

.placeholder-row {
  opacity: 0.4;
}

/* Positioning context for the scroll-edge shadows below — a plain wrapper
   around .table-scroll-wrapper so the shadows can sit as its SIBLINGS,
   painted above the table itself regardless of striping or sticky-cell
   backgrounds inside it. */
.table-scroll-outer {
  position: relative;
  max-width: 100%;
}

.table-scroll-wrapper {
  /* `overflow-x: auto` alone forces the browser to also make this a
     vertical scroll container (per spec, an axis can't stay `visible`
     once the other is scrollable) — but with no height limit it never
     actually scrolls, so the sticky header has nothing to stick against.
     Bound the height and let it scroll both ways internally instead. */
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  max-width: 100%;
  max-height: 70vh;
  border: var(--border);
  border-radius: var(--border-radius);
}

/* Horizontal-scroll hint: fades in at whichever edge still has more columns
   past it, and disappears once scrolled all the way to that side — see
   useScrollEdges (src/use_scroll_edges.js) for how canScrollLeft/Right are
   tracked. */
.scroll-shadow {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 28px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s ease;
  z-index: 3;
}

.scroll-shadow.visible {
  opacity: 1;
}

.scroll-shadow-left {
  left: 0;
  background: linear-gradient(to right, rgba(0, 0, 0, 0.4), transparent);
}

.scroll-shadow-right {
  right: 0;
  background: linear-gradient(to left, rgba(0, 0, 0, 0.4), transparent);
}

table {
  white-space: nowrap;
  /* No min-width — the table should only be as wide as its own (nowrap)
     cell content actually needs (see full_marks.vue's identical table
     rule for why). */
  border-collapse: separate;
  border-spacing: 0;
}

th, td {
  text-align: center;
}

thead th {
  position: sticky;
  top: 0;
  background: var(--color-primary);
  color: var(--color-text-light);
  z-index: 1;
}

tbody tr:nth-child(even) {
  background: var(--color-bg-alt);
}

/* Sticky Distance column — stays visible while scrolling through the
   results table, so you can always tell which row you're looking at. */
th:first-child,
td:first-child {
  position: sticky;
  left: 0;
  z-index: 1;
  box-shadow: 2px 0 4px -2px rgba(0, 0, 0, 0.15);
  will-change: transform;
}

thead th:first-child {
  z-index: 2; /* top-left corner: above both the sticky header row and column */
}

td:first-child {
  background: var(--color-bg);
}

tbody tr:nth-child(even) td:first-child {
  background: var(--color-bg-alt);
}
</style>
